/**
 * Subscription Plan Limits Configuration
 *
 * Defines the limits for each subscription tier in Trade Loop.
 * These limits control:
 * - Monthly referral sending limits
 * - Total connection limits
 * - Stripe integration for payments
 * - Other plan-specific features
 *
 * NOTE: This is a local copy for the web frontend. The source of truth
 * is /config/subscription-plan-limits.ts, but Vite cannot import CommonJS
 * modules from outside the project, so we maintain this ESM version.
 *
 * STRIPE IDS ARE NULL HERE ON PURPOSE (Card 19 task A).
 * They used to be literals from the old AIG sandbox Stripe account. Stripe object ids are
 * account-scoped, and this file is COMPILED INTO THE SHIPPED MOBILE BINARY — so a stale id in
 * here cannot be corrected without a new store build or an OTA bundle, long after the API has
 * moved to a different account.
 *
 * Nothing in the app ever read them: checkout posts a plan NAME ('pro' | 'grow') to
 * /api/workflows/subscriptions/*, and the server resolves the price id from its own env. Live
 * prices come from GET /api/workflows/pricing/plans via the usePricing() hook. Keeping the ids
 * null keeps it that way — if you find yourself wanting to put one back, add a field to the
 * pricing endpoint instead. The client must never be the thing that decides what to charge.
 */

export interface PlanLimits {
  monthlyReferrals: number;
  totalConnections: number;
  tradesLimit: number;
  features: string[];
}

/**
 * Feature Flags - Controls access to premium features based on plan tier
 *
 * NEW FEATURE MATRIX (2024):
 * - Referrals/Connections: UNLIMITED for all plans
 * - Profile Tiers: Basic (Starter) vs Professional (Pro/Grow)
 * - Trades: Starter limited to 1, Pro/Grow unlimited
 * - Verified Badge: Pro/Grow only (Stripe Identity)
 * - Team System: Grow only with Organization roles
 */
export interface FeatureFlags {
  // Profile Features
  professionalProfile: boolean;     // Company logo, company bio, professional fields
  verifiedBadge: boolean;           // Eligibility for Stripe Identity verification
  unlimitedTrades: boolean;         // Multiple trades (Starter: 1 only)

  // Analytics Features
  advancedAnalytics: boolean;       // Advanced analytics dashboard (conversion rates, trends, etc.)

  // Partner Features
  partnerFavorites: boolean;        // Preferred partner lists

  // Referral Features
  availabilityRequests: boolean;    // Send availability requests to multiple partners before sharing client details (Pro/Grow)

  // Support Features
  prioritySupport: boolean;         // Priority customer support
  premiumSupport: boolean;          // 24/7 premium support

  // Team/Organization Features (Grow Only)
  teamCollaboration: boolean;       // Multi-user team system with org roles
  customIntegrations: boolean;      // API access and custom integrations
  dedicatedAccountManager: boolean; // Dedicated support representative
}

export interface StripePlanConfig {
  stripePriceId: string | null;       // Stripe Price ID (price_xxx) - null for free plans
  stripeProductId: string | null;     // Stripe Product ID (prod_xxx) - null for free plans
  priceUSD: number;                   // Price in USD (0 for free)
  billingCycle: 'monthly' | 'free';   // Billing frequency
}

/**
 * Team Seat Configuration for Grow Plan
 *
 * Grow plan includes 5 users total (1 admin + 4 team members).
 * Additional seats can be purchased at $5/user/month.
 * Hard cap of 16 total users per organization.
 */
export const TEAM_SEAT_CONFIG = {
  /** Number of seats included with Grow plan (admin + team members) */
  INCLUDED_SEATS: 5,
  /** Maximum additional seats that can be purchased */
  MAX_ADDITIONAL_SEATS: 11,
  /** Absolute maximum users per organization (5 included + 11 additional) */
  MAX_TOTAL_SEATS: 16,
  /** Cost per additional seat per month in USD */
  ADDITIONAL_SEAT_COST: 5.00
} as const;

export interface SubscriptionPlan extends PlanLimits, StripePlanConfig {
  name: string;
  description: string;
  recommended?: boolean;
  visible?: boolean;                  // Controls if plan is shown to users (default: true)
  featureFlags: FeatureFlags;         // Boolean flags for feature gating
  tradesLimit: number;                // Number of trades allowed (Infinity = unlimited)
}

/**
 * Plan Visibility Configuration
 * Controls which subscription plans are visible to users in the UI
 * Set to false to hide a plan while keeping the code intact for future use
 */
export const PLAN_VISIBILITY = {
  starter: true,   // Always visible (free tier)
  pro: true,       // Available for v1 launch
  grow: true,      // Visible, but NOT purchasable — see PLAN_PURCHASABILITY below
} as const;

/**
 * Plan Purchasability (Card 24)
 *
 * VISIBILITY AND PURCHASABILITY ARE DIFFERENT FACTS, and conflating them is how Grow ended up
 * either invisible or fully buyable with nothing in between. `PLAN_VISIBILITY` decides whether a
 * plan appears on the plan screen at all; this decides whether a purchase of it may be started.
 * Grow is deliberately visible-but-not-purchasable: shown with its feature list, labelled
 * "Coming Soon", with no reachable checkout on web or native.
 *
 * A plan that looks purchasable and dead-ends is an App Review Guideline 2.1 "incomplete
 * features" finding, so "no reachable checkout" is the hard gate, not a disabled button. The
 * plan card, the upgrade CTAs, the native link-out and the API allowlist all read THIS flag, so
 * flipping Grow live later is one line here rather than a hunt for scattered `plan === 'grow'`
 * checks. Mirrored server-side in /config/subscription-plan-limits.ts — the client is never the
 * thing that decides what may be bought, it only agrees with the server about it.
 */
export const PLAN_PURCHASABILITY = {
  starter: true,   // Free tier — selectable (a downgrade), never a checkout
  pro: true,       // The only plan that takes money at launch
  grow: false,     // Coming Soon. Flip to true when Grow actually ships.
} as const;

/**
 * May a checkout session, plan change or hand-off token be created FOR this plan?
 * Unknown plan names are never purchasable — the allowlist fails closed.
 */
export function isPlanPurchasable(plan: string): boolean {
  return PLAN_PURCHASABILITY[plan as keyof typeof PLAN_PURCHASABILITY] === true;
}

/** Shown on the plan screen but not buyable yet — i.e. render it with a "Coming Soon" label. */
export function isPlanComingSoon(plan: string): boolean {
  return PLAN_VISIBILITY[plan as keyof typeof PLAN_VISIBILITY] === true && !isPlanPurchasable(plan);
}

/**
 * Free Trial Configuration
 * Controls whether new users can start a free Pro trial during signup
 * Set to false to disable free trials for beta release
 */
export const FREE_TRIAL_ENABLED = false;

/**
 * SUBSCRIPTION PLANS - NEW FEATURE MATRIX (2024)
 *
 * Key changes from old model:
 * - Referrals/Connections: UNLIMITED for ALL plans
 * - Profile Tiers: Basic (Starter) vs Professional (Pro/Grow)
 * - Trades: Starter limited to 1, Pro/Grow unlimited
 * - Verified Badge: Pro/Grow only (Stripe Identity)
 * - Team System: Grow only with Organization Admin/Member roles
 */
export const SUBSCRIPTION_PLANS: Record<string, SubscriptionPlan> = {
  starter: {
    name: 'Starter',
    description: 'Perfect for individuals getting started with referrals',
    monthlyReferrals: Infinity,      // UNLIMITED for all plans
    totalConnections: Infinity,       // UNLIMITED for all plans
    tradesLimit: 0,                   // No additional trades for Starter
    priceUSD: 0,
    billingCycle: 'free',
    stripePriceId: null,
    stripeProductId: null,
    features: [
      'Unlimited referrals',
      'Unlimited connections',
      'Basic profile',
      'Standard messaging',
      'Community support'
    ],
    featureFlags: {
      // Profile Features
      professionalProfile: false,     // Basic profile only
      verifiedBadge: false,           // Not eligible for verification
      unlimitedTrades: false,         // Limited to 1 trade

      // Analytics Features
      advancedAnalytics: false,       // Basic analytics only

      // Partner Features
      partnerFavorites: false,        // No partner lists

      // Referral Features
      availabilityRequests: false,    // No availability requests (Pro/Grow only)

      // Support Features
      prioritySupport: false,
      premiumSupport: false,

      // Team Features
      teamCollaboration: false,
      customIntegrations: false,
      dedicatedAccountManager: false
    }
  },
  pro: {
    name: 'Pro',
    description: 'Professional features for growing your referral network',
    recommended: true,
    monthlyReferrals: Infinity,       // UNLIMITED for all plans
    totalConnections: Infinity,        // UNLIMITED for all plans
    tradesLimit: Infinity,             // Unlimited trades
    priceUSD: 29,
    billingCycle: 'monthly',
    // Deliberately null on the client — see the STRIPE IDS note at the top of this file.
    stripePriceId: null,
    stripeProductId: null,
    features: [
      'Unlimited referrals',
      'Unlimited connections',
      'Professional profile',
      'Verified badge eligibility',
      'Unlimited trades',
      'Preferred partner lists',
      'Priority support'
    ],
    featureFlags: {
      // Profile Features
      professionalProfile: true,      // Company logo, bio, professional fields
      verifiedBadge: true,            // Eligible for Stripe Identity verification
      unlimitedTrades: true,          // Unlimited trades

      // Analytics Features
      advancedAnalytics: true,        // Full analytics dashboard

      // Partner Features
      partnerFavorites: true,         // Preferred partner lists

      // Referral Features
      availabilityRequests: true,     // Can send availability requests to multiple partners

      // Support Features
      prioritySupport: true,
      premiumSupport: false,

      // Team Features (Not in Pro)
      teamCollaboration: false,
      customIntegrations: false,
      dedicatedAccountManager: false
    }
  },
  grow: {
    name: 'Grow',
    description: 'Team features for businesses with multiple users',
    visible: true,                     // Now visible!
    monthlyReferrals: Infinity,        // UNLIMITED for all plans
    totalConnections: Infinity,         // UNLIMITED for all plans
    tradesLimit: Infinity,              // Unlimited trades
    priceUSD: 79,
    billingCycle: 'monthly',
    stripePriceId: null,
    stripeProductId: null,
    features: [
      'Everything in Pro',
      'Team collaboration (5 users total, including admin)',
      'Additional users at $5/user/month',
      'Organization Admin & Member roles',
      'Custom integrations',
      'Dedicated account manager',
      '24/7 premium support'
    ],
    featureFlags: {
      // Profile Features (same as Pro)
      professionalProfile: true,       // Org Admin has professional profile
      verifiedBadge: true,             // Eligible for Stripe Identity verification
      unlimitedTrades: true,           // Unlimited trades

      // Analytics Features
      advancedAnalytics: true,         // Full analytics dashboard

      // Partner Features
      partnerFavorites: true,          // Preferred partner lists

      // Referral Features
      availabilityRequests: true,      // Can send availability requests to multiple partners

      // Support Features
      prioritySupport: true,
      premiumSupport: true,            // 24/7 premium support

      // Team Features (Grow Exclusive)
      teamCollaboration: true,         // Multi-user team system
      customIntegrations: true,        // API access
      dedicatedAccountManager: true    // Dedicated support
    }
  }
};

/**
 * Legacy PLAN_LIMITS for backward compatibility
 * @deprecated Use SUBSCRIPTION_PLANS instead
 */
export const PLAN_LIMITS: Record<string, PlanLimits> = {
  starter: {
    monthlyReferrals: SUBSCRIPTION_PLANS.starter.monthlyReferrals,
    totalConnections: SUBSCRIPTION_PLANS.starter.totalConnections,
    tradesLimit: SUBSCRIPTION_PLANS.starter.tradesLimit,
    features: SUBSCRIPTION_PLANS.starter.features
  },
  pro: {
    monthlyReferrals: SUBSCRIPTION_PLANS.pro.monthlyReferrals,
    totalConnections: SUBSCRIPTION_PLANS.pro.totalConnections,
    tradesLimit: SUBSCRIPTION_PLANS.pro.tradesLimit,
    features: SUBSCRIPTION_PLANS.pro.features
  },
  grow: {
    monthlyReferrals: SUBSCRIPTION_PLANS.grow.monthlyReferrals,
    totalConnections: SUBSCRIPTION_PLANS.grow.totalConnections,
    tradesLimit: SUBSCRIPTION_PLANS.grow.tradesLimit,
    features: SUBSCRIPTION_PLANS.grow.features
  }
};

/**
 * Get plan limits for a specific subscription plan
 */
export function getPlanLimits(plan: string): PlanLimits {
  return PLAN_LIMITS[plan] || PLAN_LIMITS.starter;
}

/**
 * Check if a user has reached their plan limit for a specific resource
 */
export function hasReachedLimit(currentUsage: number, plan: string, resource: 'monthlyReferrals' | 'totalConnections' | 'tradesLimit'): boolean {
  const limits = getPlanLimits(plan);
  const limit = limits[resource];
  if (limit === Infinity || !Number.isFinite(limit)) {
    return false;
  }
  return currentUsage >= limit;
}

/**
 * Calculate usage percentage for progress bars
 */
export function calculateUsagePercentage(currentUsage: number, plan: string, resource: 'monthlyReferrals' | 'totalConnections' | 'tradesLimit'): number {
  const limits = getPlanLimits(plan);
  const limit = limits[resource];
  if (limit === Infinity || !Number.isFinite(limit)) {
    return 0;
  }
  return Math.min((currentUsage / limit) * 100, 100);
}

/**
 * Check if a resource is unlimited for a plan
 */
export function isResourceUnlimited(plan: string, resource: 'monthlyReferrals' | 'totalConnections' | 'tradesLimit'): boolean {
  const limits = getPlanLimits(plan);
  const limit = limits[resource];
  return limit === Infinity || !Number.isFinite(limit);
}

/**
 * Get the trade limit for a plan
 */
export function getTradeLimit(plan: string): number {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.tradesLimit;
}

/**
 * Check if a user can add more trades
 */
export function canAddTrade(currentCount: number, plan: string): boolean {
  const limit = getTradeLimit(plan);
  if (limit === Infinity || !Number.isFinite(limit)) {
    return true;
  }
  return currentCount < limit;
}

/**
 * Get full subscription plan details including Stripe metadata
 */
export function getSubscriptionPlan(plan: string): SubscriptionPlan {
  return SUBSCRIPTION_PLANS[plan] || SUBSCRIPTION_PLANS.starter;
}

/**
 * Get Stripe Price ID for a plan (null for free plans)
 */
export function getStripePriceId(plan: string): string | null {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.stripePriceId;
}

/**
 * Get Stripe Product ID for a plan (null for free plans)
 */
export function getStripeProductId(plan: string): string | null {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.stripeProductId;
}

/**
 * Check if a plan requires Stripe payment
 */
export function isPaidPlan(plan: string): boolean {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.priceUSD > 0;
}

/**
 * Check if a plan is an upgrade from the current plan
 */
export function isUpgrade(currentPlan: string, newPlan: string): boolean {
  const planHierarchy = ['starter', 'pro', 'grow'];
  const currentIndex = planHierarchy.indexOf(currentPlan);
  const newIndex = planHierarchy.indexOf(newPlan);
  return newIndex > currentIndex;
}

/**
 * Check if a plan is a downgrade from the current plan
 */
export function isDowngrade(currentPlan: string, newPlan: string): boolean {
  const planHierarchy = ['starter', 'pro', 'grow'];
  const currentIndex = planHierarchy.indexOf(currentPlan);
  const newIndex = planHierarchy.indexOf(newPlan);
  return newIndex < currentIndex;
}

/**
 * Get all available plans as an array
 */
export function getAllPlans(): SubscriptionPlan[] {
  return Object.keys(SUBSCRIPTION_PLANS).map(key => SUBSCRIPTION_PLANS[key]);
}

/**
 * Format plan price for display
 */
export function formatPlanPrice(plan: string): string {
  const planConfig = getSubscriptionPlan(plan);
  if (planConfig.priceUSD === 0) {
    return 'Free';
  }
  return `$${planConfig.priceUSD}/month`;
}

/**
 * Calculate when a plan change will take effect
 */
export function getPlanChangeEffectiveDate(currentPeriodEnd: Date): Date {
  return currentPeriodEnd;
}

/**
 * Calculate what the user will pay on their next billing cycle after a plan change
 */
export function getNextBillingAmount(newPlan: string): number {
  const planConfig = getSubscriptionPlan(newPlan);
  return planConfig.priceUSD;
}

/**
 * Check if a plan has a specific feature enabled
 */
export function hasFeature(plan: string, featureName: keyof FeatureFlags): boolean {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.featureFlags[featureName];
}

/**
 * Get all enabled features for a plan
 */
export function getEnabledFeatures(plan: string): string[] {
  const planConfig = getSubscriptionPlan(plan);
  return Object.entries(planConfig.featureFlags)
    .filter(([_, enabled]) => enabled)
    .map(([feature]) => feature);
}

/**
 * Get the feature flags object for a plan
 */
export function getFeatureFlags(plan: string): FeatureFlags {
  const planConfig = getSubscriptionPlan(plan);
  return planConfig.featureFlags;
}

// ============================================================================
// TEAM SEAT MANAGEMENT HELPERS
// ============================================================================

/**
 * Check if an organization can add more team members
 */
export function canAddTeamMember(currentMemberCount: number, totalSeats: number = TEAM_SEAT_CONFIG.INCLUDED_SEATS): boolean {
  const availableMemberSlots = totalSeats - 1;
  return currentMemberCount < availableMemberSlots;
}

/**
 * Get remaining available team member slots
 */
export function getRemainingTeamSlots(currentMemberCount: number, totalSeats: number = TEAM_SEAT_CONFIG.INCLUDED_SEATS): number {
  const availableMemberSlots = totalSeats - 1;
  return Math.max(0, availableMemberSlots - currentMemberCount);
}

/**
 * Calculate additional seats needed to add more members
 */
export function getAdditionalSeatsNeeded(
  currentMemberCount: number,
  membersToAdd: number,
  totalSeats: number = TEAM_SEAT_CONFIG.INCLUDED_SEATS
): number {
  const availableMemberSlots = totalSeats - 1;
  const slotsNeeded = currentMemberCount + membersToAdd;
  if (slotsNeeded <= availableMemberSlots) {
    return 0;
  }
  return slotsNeeded - availableMemberSlots;
}

/**
 * Check if adding seats would exceed the hard cap
 */
export function wouldExceedMaxSeats(currentTotalSeats: number, seatsToAdd: number): boolean {
  return (currentTotalSeats + seatsToAdd) > TEAM_SEAT_CONFIG.MAX_TOTAL_SEATS;
}

/**
 * Calculate monthly cost for additional seats
 */
export function calculateAdditionalSeatsCost(additionalSeats: number): number {
  return additionalSeats * TEAM_SEAT_CONFIG.ADDITIONAL_SEAT_COST;
}
